import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { EnrichmentVariants } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  loading: boolean;
  // (Default enrichment props remain in the interface but are no longer used in the UI)
  apiKey: string;
  variant: EnrichmentVariants;
  // Prospeos enrichment props
  prospeosEnrichEmail: boolean;
  prospeosEnrichPhone: boolean;
  // AI Research Agent props
  aiResearchFullFunnel: boolean;
  // Custom field enrichment props
  customFieldPersonPrompt: string;
  customFieldCompanyPrompt: string;
  customFieldOverwriteExisting: boolean;

  // Handlers for default enrichment (no longer used in the UI)
  handleApiKeyChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleVariantChange(event: React.ChangeEvent<HTMLInputElement>): void;
  // Handlers for Prospeos enrichment
  handleProspeosEnrichEmailChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleProspeosEnrichPhoneChange(event: React.ChangeEvent<HTMLInputElement>): void;
  // Handler for AI Research Agent
  handleAiResearchFullFunnelChange(event: React.ChangeEvent<HTMLInputElement>): void;
  // Handlers for custom field enrichment
  handleCustomFieldPersonPromptChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleCustomFieldCompanyPromptChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleCustomFieldOverwriteExistingChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;
  // Unified start function – parent decides which backend call to trigger
  handleStartEnrichment(activeTab: number): void;
}

// Helper component to render a tab panel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`enrichment-tabpanel-${index}`} aria-labelledby={`enrichment-tab-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const EnrichContactsDialog: React.FC<Props> = ({
  open,
  loading,
  prospeosEnrichEmail,
  prospeosEnrichPhone,
  aiResearchFullFunnel,
  customFieldPersonPrompt,
  customFieldCompanyPrompt,
  customFieldOverwriteExisting,
  handleProspeosEnrichEmailChange,
  handleProspeosEnrichPhoneChange,
  handleAiResearchFullFunnelChange,
  handleCustomFieldPersonPromptChange,
  handleCustomFieldCompanyPromptChange,
  handleCustomFieldOverwriteExistingChange,
  handleClose,
  handleStartEnrichment,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth maxWidth="md">
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('aiPoweredEnrichmentTitle')}</Typography>
      </DialogTitle>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="Enrichment Tabs"
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label={t('prospeosEnrichmentTab')} id="enrichment-tab-0" aria-controls="enrichment-tabpanel-0" />
        <Tab label={t('customFieldEnrichmentTab')} id="enrichment-tab-1" aria-controls="enrichment-tabpanel-1" />
      </Tabs>
      <Divider />
      <DialogContent className={classes.content}>
        {/* Prospeos Enrichment Tab */}
        <TabPanel value={activeTab} index={0}>
          <Stack gap={3}>
            {/* AI Research Agent – Free option */}
            <Stack gap={2}>
              <div>
                <Typography className={classes.subTitle} sx={{ marginBottom: '8px' }}>
                  {t('aiResearchAgentTitle')}
                </Typography>
                <Typography className={classes.greyText}>{t('aiResearchAgentDescription')}</Typography>
              </div>
              <PaiCheckbox
                label={t('enrichFullFunnel')}
                checked={aiResearchFullFunnel}
                onChange={handleAiResearchFullFunnelChange}
                disabled={prospeosEnrichEmail || prospeosEnrichPhone}
              />
            </Stack>
            {/* When AI research chosen, Prospeos options disabled */}
            <Divider />
            <Stack gap={2}>
              <div>
                <Typography className={classes.subTitle} sx={{ marginBottom: '8px' }}>
                  {t('prospeosEnrichmentTitle')}
                </Typography>
                <Typography className={classes.greyText}>{t('prospeosEnrichmentDescription')}</Typography>
              </div>
              <Stack direction={'row'} gap={2}>
                <PaiCheckbox
                  label={t('enrichEmail')}
                  checked={prospeosEnrichEmail}
                  onChange={handleProspeosEnrichEmailChange}
                  disabled={aiResearchFullFunnel}
                />
                <PaiCheckbox
                  label={t('enrichPhoneNumber')}
                  checked={prospeosEnrichPhone}
                  onChange={handleProspeosEnrichPhoneChange}
                  disabled={aiResearchFullFunnel}
                />
              </Stack>
            </Stack>
          </Stack>
        </TabPanel>

        {/* Custom Field Enrichment Tab */}
        <TabPanel value={activeTab} index={1}>
          <Stack gap={3}>
            <Stack gap={2}>
              <div>
                <Typography className={classes.subTitle} sx={{ marginBottom: '8px' }}>
                  {t('customFieldEnrichmentTitle')}
                </Typography>
                <Typography className={classes.greyText}>{t('customFieldEnrichmentDescription')}</Typography>
              </div>
            </Stack>
            <Divider />
            <PaiTextArea
              value={customFieldPersonPrompt}
              label={t('customFieldPersonPromptLabel')}
              placeholder={t('customFieldPersonPromptPlaceholder')}
              variant="white"
              onChange={handleCustomFieldPersonPromptChange}
            />
            <PaiTextArea
              value={customFieldCompanyPrompt}
              label={t('customFieldCompanyPromptLabel')}
              placeholder={t('customFieldCompanyPromptPlaceholder')}
              variant="white"
              onChange={handleCustomFieldCompanyPromptChange}
            />
            <PaiCheckbox
              label={t('overrideExistingResults')}
              checked={customFieldOverwriteExisting}
              onChange={handleCustomFieldOverwriteExistingChange}
            />
          </Stack>
        </TabPanel>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size="sm" underline="always" action={handleClose} />
        <PaiButton
          text={t('startEnrichment')}
          action={() => handleStartEnrichment(activeTab)}
          size="small"
          border="rounded"
          disabled={
            (activeTab === 0 && !(aiResearchFullFunnel || prospeosEnrichEmail || prospeosEnrichPhone)) ||
            (activeTab === 1 && !customFieldPersonPrompt.trim() && !customFieldCompanyPrompt.trim())
          }
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.grey[600],
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default EnrichContactsDialog;
